/**
 * ParcelBookingPage — multi-step wizard for booking a parcel from home (F-383).
 *
 * Flow:
 *   1. Parcel Details   — package type, weight, dimensions, service class,
 *                         destination state.
 *   2. Sender & Receiver — contact + address blocks for both parties.
 *   3. Quote & Submit   — fetches an instant quote (GET /v1/parcels-mine/quote)
 *                         and submits (POST /v1/parcels-mine) — citizen endpoint,
 *                         no operator RBAC required.
 *
 * Backend wiring (F-383 — LIVE, not deferred):
 *   - service-mail   POST /v1/parcels-mine          (`Parcel/create-own`).
 *   - service-mail   GET  /v1/parcels-mine/quote    (`Parcel/read-own`).
 *
 * Both permissions are seeded to the `customer` role by 20260507_customer_role.sql.
 * The booking deducts from the citizen's NIPOST Pay wallet (NIPOST_PAY method).
 *
 * Quote is a stub zone-based tariff (same-state ₦1,500 / diff-state ₦3,000 /
 * cross-region ₦5,000 × service-class multiplier). Real F-001 pricing is a
 * follow-up slice.
 */
import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  MapPin,
  Package,
  User,
} from 'lucide-react';

import { AddressFields, type AddressValue, EMPTY_ADDRESS } from '@upmp/ui';

import { ApiError } from '../../lib/api-client';
import {
  bookParcel,
  getParcelQuote,
  type ParcelBookingResponse,
  type ParcelQuoteResponse,
} from '../../lib/api/parcel-booking';
import {
  initializeCardPayment,
  isTestModeAuthorizationUrl,
  loadPaystackInline,
  verifyCardPayment,
} from '../../lib/api/card-payments';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { env } from '../../lib/env';
import { PageHeader } from './PortalUI';

type PaymentChoice = 'NIPOST_PAY' | 'CARD';

/** Best-effort email extraction from the customer JWT — Paystack initialize
 *  needs an email and we don't want to prompt the user mid-checkout. */
function emailFromToken(token: string | null): string {
  if (!token) return '';
  const parts = token.split('.');
  if (parts.length !== 3) return '';
  const segment = parts[1] ?? '';
  try {
    let s = segment.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4 !== 0) s += '=';
    const payload = JSON.parse(atob(s)) as Record<string, unknown>;
    const email = payload['email'] ?? payload['preferred_username'];
    return typeof email === 'string' ? email : '';
  } catch {
    return '';
  }
}

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

const STEPS: readonly { readonly number: number; readonly label: string }[] = [
  { number: 1, label: 'Parcel Details' },
  { number: 2, label: 'Sender & Receiver' },
  { number: 3, label: 'Quote & Submit' },
];

const NIGERIAN_STATES: readonly { readonly value: string; readonly label: string; readonly zone: string }[] = [
  { value: 'lagos',   label: 'Lagos',     zone: 'NG_SOUTH_WEST' },
  { value: 'abuja',   label: 'FCT Abuja', zone: 'NG_NORTH_CENTRAL' },
  { value: 'kano',    label: 'Kano',      zone: 'NG_NORTH_WEST' },
  { value: 'rivers',  label: 'Rivers',    zone: 'NG_SOUTH_SOUTH' },
  { value: 'oyo',     label: 'Oyo',       zone: 'NG_SOUTH_WEST' },
  { value: 'anambra', label: 'Anambra',   zone: 'NG_SOUTH_EAST' },
];

type PackageType = 'LETTER' | 'PARCEL' | 'EMS';
type ServiceType = 'STANDARD' | 'EXPRESS' | 'EMS' | 'REGISTERED_MAIL';

interface FormState {
  packageType: PackageType;
  weight: string;
  length: string;
  width: string;
  height: string;
  serviceType: ServiceType;
  destState: string;
  destCity: string;
  senderName: string;
  senderPhone: string;
  senderEmail: string;
  senderAddress: string;
  senderCity: string;
  senderState: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  receiverCity: string;
  contentDescription: string;
}

const INITIAL_FORM: FormState = {
  packageType: 'PARCEL',
  weight: '',
  length: '',
  width: '',
  height: '',
  serviceType: 'STANDARD',
  destState: '',
  destCity: '',
  senderName: '',
  senderPhone: '',
  senderEmail: '',
  senderAddress: '',
  senderCity: 'Lagos',
  senderState: 'Lagos',
  receiverName: '',
  receiverPhone: '',
  receiverAddress: '',
  receiverCity: '',
  contentDescription: '',
};

 
function naira(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) {
    return '₦ 0.00';
  }
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatBackendError(err: Error | null): string | null {
  if (!err) return null;
  if (err instanceof ApiError && err.status === 402) {
    return `Insufficient wallet balance: ${err.message} Please top up your NIPOST Pay wallet and try again.`;
  }
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return 'Authentication required. Please sign in and ensure your NIPOST Pay wallet is activated.';
  }
  if (err instanceof ApiError && err.status === 422) {
    return `Payment error: ${err.message}`;
  }
  return err.message;
}

export function ParcelBookingPage(): JSX.Element {
  const { token } = useCustomerAuth();
  const [step, setStep] = useState<number>(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  // service-geo cascading addresses for sender + receiver. The legacy
  // free-text city/state inputs are kept for back-compat with the existing
  // form state; on submit we hoist stateName / lgaName onto them so the
  // backend keeps receiving the existing shape AND the new geo_* fields.
  const [senderAddress, setSenderAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [receiverAddress, setReceiverAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [quote, setQuote] = useState<ParcelQuoteResponse | null>(null);
  const [booking, setBooking] = useState<ParcelBookingResponse | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentChoice>('NIPOST_PAY');
  const [cardPaymentReference, setCardPaymentReference] = useState<string | null>(null);
  const [cardPaymentError, setCardPaymentError] = useState<string | null>(null);
  const [cardPaymentInProgress, setCardPaymentInProgress] = useState<boolean>(false);

  const customerEmail = useMemo(() => emailFromToken(token), [token]);

  function update<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const canProceed = useMemo<boolean>(() => {
    if (step === 1) return form.weight.trim().length > 0 && form.destState.length > 0 && form.destCity.trim().length > 0;
    if (step === 2)
      return (
        form.senderName.trim().length > 0 &&
        form.senderPhone.trim().length > 0 &&
        form.senderAddress.trim().length > 0 &&
        form.receiverName.trim().length > 0 &&
        form.receiverPhone.trim().length > 0 &&
        form.receiverAddress.trim().length > 0 &&
        form.contentDescription.trim().length > 0
      );
    return true;
  }, [step, form]);

  // Fetch a quote (GET /v1/parcels-mine/quote) when the user lands on step 3.
  const quoteMutation = useMutation<ParcelQuoteResponse, Error, undefined>({
    mutationFn: async () => {
      const receiverStateValue = NIGERIAN_STATES.find((s) => s.value === form.destState)?.value ?? form.destState;
      return getParcelQuote(
        form.senderState.toLowerCase(),
        receiverStateValue.toLowerCase(),
        form.serviceType,
      );
    },
    onSuccess: (data) => setQuote(data),
  });

  // Submit booking (POST /v1/parcels-mine).
  // Variables: payment method + (when CARD) the verified Paystack reference
  // are passed in explicitly so the post-card-payment submit doesn't race with
  // React state propagation.
  interface BookVars {
    paymentMethod: PaymentChoice;
    cardPaymentReference?: string;
  }
  const bookMutation = useMutation<ParcelBookingResponse, Error, BookVars>({
    mutationFn: async (vars) => {
      const receiverStateLabel = NIGERIAN_STATES.find((s) => s.value === form.destState)?.label ?? form.destState;
      // Hoist the geo selection onto the legacy free-text fields when present
      // so the wire payload retains the existing shape AND carries soft-FK IDs.
      const senderStateText =
        senderAddress.stateName ?? form.senderState;
      const senderCityText =
        senderAddress.lgaName ?? senderAddress.stateName ?? form.senderCity ?? form.senderState;
      const receiverStateText =
        receiverAddress.stateName ?? receiverStateLabel;
      const receiverCityText =
        receiverAddress.lgaName ??
        receiverAddress.stateName ??
        (form.receiverCity || form.destCity);
      return bookParcel({
        sender: {
          name: form.senderName,
          phone: form.senderPhone,
          email: form.senderEmail || undefined,
          addressLine1: senderAddress.addressLine1 || form.senderAddress,
          city: senderCityText,
          state: senderStateText,
          ...(senderAddress.postalCode ? { postalCode: senderAddress.postalCode } : {}),
          ...(senderAddress.countryId !== null ? { geoCountryId: senderAddress.countryId } : {}),
          ...(senderAddress.countryName !== null ? { geoCountryName: senderAddress.countryName } : {}),
          ...(senderAddress.stateId !== null ? { geoStateId: senderAddress.stateId } : {}),
          ...(senderAddress.stateName !== null ? { geoStateName: senderAddress.stateName } : {}),
          ...(senderAddress.lgaId !== null ? { geoLgaId: senderAddress.lgaId } : {}),
          ...(senderAddress.lgaName !== null ? { geoLgaName: senderAddress.lgaName } : {}),
          ...(senderAddress.wardId !== null ? { geoWardId: senderAddress.wardId } : {}),
          ...(senderAddress.wardName !== null ? { geoWardName: senderAddress.wardName } : {}),
        },
        receiver: {
          name: form.receiverName,
          phone: form.receiverPhone,
          addressLine1: receiverAddress.addressLine1 || form.receiverAddress,
          city: receiverCityText,
          state: receiverStateText,
          ...(receiverAddress.postalCode ? { postalCode: receiverAddress.postalCode } : {}),
          ...(receiverAddress.countryId !== null ? { geoCountryId: receiverAddress.countryId } : {}),
          ...(receiverAddress.countryName !== null ? { geoCountryName: receiverAddress.countryName } : {}),
          ...(receiverAddress.stateId !== null ? { geoStateId: receiverAddress.stateId } : {}),
          ...(receiverAddress.stateName !== null ? { geoStateName: receiverAddress.stateName } : {}),
          ...(receiverAddress.lgaId !== null ? { geoLgaId: receiverAddress.lgaId } : {}),
          ...(receiverAddress.lgaName !== null ? { geoLgaName: receiverAddress.lgaName } : {}),
          ...(receiverAddress.wardId !== null ? { geoWardId: receiverAddress.wardId } : {}),
          ...(receiverAddress.wardName !== null ? { geoWardName: receiverAddress.wardName } : {}),
        },
        package: {
          weightKg: Math.max(0.1, parseFloat(form.weight) || 0.1),
          lengthCm: parseFloat(form.length) || 1,
          widthCm: parseFloat(form.width) || 1,
          heightCm: parseFloat(form.height) || 1,
          contentDescription: form.contentDescription,
        },
        serviceClass: form.serviceType,
        paymentMethod: vars.paymentMethod,
        ...(vars.paymentMethod === 'CARD' && vars.cardPaymentReference
          ? { cardPaymentReference: vars.cardPaymentReference }
          : {}),
      });
    },
    onSuccess: (data) => setBooking(data),
  });

  useEffect(() => {
    if (step === 3 && quote === null && !quoteMutation.isPending && !quoteMutation.isError) {
      quoteMutation.mutate(undefined);
    }
  }, [step, quote, quoteMutation]);

  function handleNext(): void {
    if (canProceed && step < 3) setStep((s) => s + 1);
  }
  function handleBack(): void {
    if (step > 1) setStep((s) => s - 1);
  }
  async function runCardPaymentFlow(amountNgn: string): Promise<string | null> {
    setCardPaymentError(null);
    setCardPaymentInProgress(true);
    try {
      const amountMinor = Math.round(parseFloat(amountNgn) * 100);
      if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
        throw new Error('Invalid amount; could not initialize card payment.');
      }
      const initResp = await initializeCardPayment({
        amountMinor,
        currency: 'NGN',
        customerEmail: customerEmail || 'citizen@upmp.test',
      });

      // Test-mode: paystack-client returns a test.paystack.local URL. Skip
      // the inline modal and verify directly. Keeps dev/e2e hermetic.
      if (isTestModeAuthorizationUrl(initResp.authorizationUrl)) {
        const verifyResp = await verifyCardPayment(initResp.reference);
        if (verifyResp.status !== 'SUCCESS') {
          throw new Error(`Card payment ${verifyResp.status.toLowerCase()}.`);
        }
        return initResp.reference;
      }

      // Live mode: open the Paystack inline modal. Resolve when the modal
      // calls onSuccess; reject on cancel / error.
      const handle = await loadPaystackInline();
      if (!handle) {
        // Fallback: open the authorizationUrl in a new tab. The user manually
        // returns and we still need to verify.
        window.open(initResp.authorizationUrl, '_blank', 'noopener,noreferrer');
      } else {
        await new Promise<void>((resolve, reject) => {
          handle.newTransaction({
            accessCode: initResp.accessCode,
            onSuccess: () => resolve(),
            onCancel: () => reject(new Error('Card payment cancelled.')),
            onError: (err) =>
              reject(err instanceof Error ? err : new Error('Card payment failed.')),
          });
        });
      }

      const verifyResp = await verifyCardPayment(initResp.reference);
      if (verifyResp.status !== 'SUCCESS') {
        throw new Error(`Card payment ${verifyResp.status.toLowerCase()}.`);
      }
      return initResp.reference;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Card payment failed.';
      setCardPaymentError(msg);
      return null;
    } finally {
      setCardPaymentInProgress(false);
    }
  }

  async function handleBook(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!quote) return;

    if (paymentMethod === 'CARD') {
      const reference = cardPaymentReference ?? (await runCardPaymentFlow(quote.amountNgn));
      if (!reference) return;
      setCardPaymentReference(reference);
      bookMutation.mutate({ paymentMethod: 'CARD', cardPaymentReference: reference });
      return;
    }

    bookMutation.mutate({ paymentMethod: 'NIPOST_PAY' });
  }

  // ── Success state ──────────────────────────────────────────────────────────

  if (booking) {
    return (
      <div>
        <PageHeader title="Booking Confirmed" description="Your parcel is registered with NIPOST." />
        <div className="max-w-2xl mx-auto bg-white rounded-xl border border-border p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-nipost-green flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Waybill Issued</h2>
          <p className="text-sm text-muted mb-4">
            {paymentMethod === 'CARD'
              ? `Payment of ${naira(booking.amountNgn)} confirmed via card.`
              : `Payment of ${naira(booking.amountNgn)} deducted from your NIPOST Pay wallet.`}
          </p>
          <div className="bg-nipost-green-light rounded-xl p-5 text-left mb-4">
            <p className="text-xs text-muted uppercase font-medium">Tracking Number</p>
            <p
              className="text-2xl font-bold font-mono text-nipost-green mt-0.5"
              data-testid="tracking-number"
            >
              {booking.trackingNumber}
            </p>
            <p className="text-xs text-muted mt-2">Status: <span className="font-mono">{booking.status}</span></p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 text-left mb-6">
            <p className="font-semibold mb-1">Drop-off Instructions</p>
            <p className="text-xs">{booking.dropoffInstructions}</p>
          </div>
          <a
            href={`/portal/tracking?tn=${encodeURIComponent(booking.trackingNumber)}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            Track this parcel <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  // ── Wizard ─────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Book a Parcel"
        description="Fill in parcel details, get an instant quote, and pay with your NIPOST Pay wallet or card."
      />

      <div className="max-w-2xl mx-auto">
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, idx) => (
            <div key={s.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                    s.number < step
                      ? 'bg-nipost-green border-nipost-green text-white'
                      : s.number === step
                        ? 'bg-white border-nipost-green text-nipost-green'
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {s.number < step ? <CheckCircle2 className="w-5 h-5" /> : s.number}
                </div>
                <span className={`text-xs mt-1.5 font-medium ${s.number <= step ? 'text-gray-900' : 'text-muted'}`}>
                  {s.label}
                </span>
              </div>
              {idx < STEPS.length - 1 ? (
                <div className={`w-16 sm:w-24 h-0.5 mx-1 sm:mx-2 mb-5 ${s.number < step ? 'bg-nipost-green' : 'bg-gray-200'}`} />
              ) : null}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-border p-6 sm:p-8">
          {step === 1 ? (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900">Parcel Details</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package Type</label>
                <div className="flex gap-2">
                  {(['LETTER', 'PARCEL', 'EMS'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => update('packageType', type)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        form.packageType === type
                          ? 'bg-nipost-green text-white border-nipost-green'
                          : 'bg-white text-gray-700 border-border hover:border-nipost-green'
                      }`}
                    >
                      {type[0] + type.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="pb-weight" className="block text-sm font-medium text-gray-700 mb-1">
                  Weight (kg) <span className="text-danger">*</span>
                </label>
                <input
                  id="pb-weight"
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={form.weight}
                  onChange={(e) => update('weight', e.target.value)}
                  placeholder="e.g. 2.5"
                  className={inputClass}
                />
              </div>

              {form.packageType !== 'LETTER' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions (cm)</label>
                  <div className="grid grid-cols-3 gap-3">
                    <input aria-label="Length" type="number" min="1" value={form.length} onChange={(e) => update('length', e.target.value)} placeholder="Length" className={inputClass} />
                    <input aria-label="Width" type="number" min="1" value={form.width} onChange={(e) => update('width', e.target.value)} placeholder="Width" className={inputClass} />
                    <input aria-label="Height" type="number" min="1" value={form.height} onChange={(e) => update('height', e.target.value)} placeholder="Height" className={inputClass} />
                  </div>
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Class</label>
                <div className="flex flex-wrap gap-2">
                  {(['STANDARD', 'EXPRESS', 'EMS', 'REGISTERED_MAIL'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => update('serviceType', s)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        form.serviceType === s
                          ? 'bg-nipost-green text-white border-nipost-green'
                          : 'bg-white text-gray-700 border-border hover:border-nipost-green'
                      }`}
                    >
                      {s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="pb-destState" className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="inline w-4 h-4 mr-1 text-muted -mt-0.5" />
                  Destination <span className="text-danger">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    id="pb-destState"
                    value={form.destState}
                    onChange={(e) => update('destState', e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select State</option>
                    {NIGERIAN_STATES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label="Destination city"
                    type="text"
                    value={form.destCity}
                    onChange={(e) => update('destCity', e.target.value)}
                    placeholder="City / LGA"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Sender &amp; Receiver Details</h2>

              <details className="group" open>
                <summary className="flex items-center justify-between cursor-pointer list-none mb-2">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <User className="w-4 h-4 text-nipost-green" /> Sender Information
                  </h3>
                </summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pb-senderName" className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-danger">*</span></label>
                    <input id="pb-senderName" type="text" value={form.senderName} onChange={(e) => update('senderName', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="pb-senderPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-danger">*</span></label>
                    <input id="pb-senderPhone" type="tel" value={form.senderPhone} onChange={(e) => update('senderPhone', e.target.value)} placeholder="+234..." className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="pb-senderEmail" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input id="pb-senderEmail" type="email" value={form.senderEmail} onChange={(e) => update('senderEmail', e.target.value)} className={inputClass} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address <span className="text-danger">*</span></label>
                    <AddressFields
                      geoBaseUrl={env.serviceGeoBaseUrl}
                      value={senderAddress}
                      onChange={(next) => {
                        setSenderAddress(next);
                        // Mirror onto the legacy free-text fields so existing
                        // wire shape + downstream code paths keep working.
                        update('senderAddress', next.addressLine1);
                        if (next.stateName) update('senderState', next.stateName);
                        const city = next.lgaName ?? next.stateName;
                        if (city) update('senderCity', city);
                      }}
                    />
                  </div>
                </div>
              </details>

              <details className="group" open>
                <summary className="flex items-center justify-between cursor-pointer list-none mb-2">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-nipost-green" /> Receiver Information
                  </h3>
                </summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pb-receiverName" className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-danger">*</span></label>
                    <input id="pb-receiverName" type="text" value={form.receiverName} onChange={(e) => update('receiverName', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="pb-receiverPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-danger">*</span></label>
                    <input id="pb-receiverPhone" type="tel" value={form.receiverPhone} onChange={(e) => update('receiverPhone', e.target.value)} className={inputClass} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address <span className="text-danger">*</span></label>
                    <AddressFields
                      geoBaseUrl={env.serviceGeoBaseUrl}
                      value={receiverAddress}
                      onChange={(next) => {
                        setReceiverAddress(next);
                        update('receiverAddress', next.addressLine1);
                        if (next.stateName) update('receiverCity', next.lgaName ?? next.stateName);
                      }}
                    />
                  </div>
                </div>
              </details>

              <div>
                <label htmlFor="pb-contents" className="block text-sm font-medium text-gray-700 mb-1">
                  Parcel contents <span className="text-danger">*</span>
                </label>
                <textarea
                  id="pb-contents"
                  rows={2}
                  value={form.contentDescription}
                  onChange={(e) => update('contentDescription', e.target.value)}
                  placeholder="e.g. Documents, electronics, clothing"
                  className={`${inputClass} resize-none`}
                />
                <p className="text-[11px] text-muted mt-1">Required by NIPOST for prohibited-items screening.</p>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <form onSubmit={handleBook} className="space-y-6" noValidate>
              <h2 className="text-lg font-semibold text-gray-900">Quote &amp; Submit</h2>

              {quoteMutation.isPending ? (
                <div className="rounded-lg bg-gray-50 p-4 flex items-center gap-2 text-sm text-muted">
                  <Loader2 className="w-4 h-4 animate-spin" /> Calculating your price…
                </div>
              ) : null}

              {quoteMutation.isError ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" role="alert">
                  {formatBackendError(quoteMutation.error)}
                </div>
              ) : null}

              {quote ? (
                <div className="bg-gray-50 rounded-xl border border-border p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Price Quote</h3>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Delivery charge ({form.serviceType.replace('_', ' ')})</span>
                    <span className="font-medium text-gray-900">{naira(quote.amountNgn)}</span>
                  </div>
                  <div className="border-t border-border pt-3 flex justify-between">
                    <span className="text-sm font-bold text-gray-900">Total</span>
                    <span className="text-lg font-bold text-nipost-green">{naira(quote.amountNgn)}</span>
                  </div>
                  <p className="text-[11px] text-muted">
                    Tariff <span className="font-mono">{quote.tariffId}</span> ·
                    Valid until {new Date(quote.quoteExpiresAt).toLocaleString()}
                  </p>
                  <p className="text-[10px] text-muted italic">{quote.note}</p>
                </div>
              ) : null}

              <div className="rounded-xl border border-border bg-white p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">Payment method</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer text-sm ${
                      paymentMethod === 'NIPOST_PAY'
                        ? 'border-nipost-green bg-nipost-green-light/30'
                        : 'border-border hover:border-nipost-green/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pb-paymentMethod"
                      value="NIPOST_PAY"
                      checked={paymentMethod === 'NIPOST_PAY'}
                      onChange={() => {
                        setPaymentMethod('NIPOST_PAY');
                        setCardPaymentReference(null);
                        setCardPaymentError(null);
                      }}
                      className="mt-0.5"
                      data-testid="pb-pm-nipost"
                    />
                    <span>
                      <span className="block font-semibold text-gray-900">NIPOST Pay wallet</span>
                      <span className="block text-[11px] text-muted">
                        Debited immediately on submission.
                      </span>
                    </span>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer text-sm ${
                      paymentMethod === 'CARD'
                        ? 'border-nipost-green bg-nipost-green-light/30'
                        : 'border-border hover:border-nipost-green/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pb-paymentMethod"
                      value="CARD"
                      checked={paymentMethod === 'CARD'}
                      onChange={() => {
                        setPaymentMethod('CARD');
                        setCardPaymentReference(null);
                        setCardPaymentError(null);
                      }}
                      className="mt-0.5"
                      data-testid="pb-pm-card"
                    />
                    <span>
                      <span className="block font-semibold text-gray-900">Card (Paystack)</span>
                      <span className="block text-[11px] text-muted">
                        Pay with any debit / credit card via Paystack.
                      </span>
                    </span>
                  </label>
                </div>
                {paymentMethod === 'CARD' && cardPaymentReference !== null ? (
                  <p className="text-[11px] text-nipost-green">
                    Card payment confirmed (<span className="font-mono">{cardPaymentReference}</span>).
                  </p>
                ) : null}
                {cardPaymentError ? (
                  <p className="text-[11px] text-red-700" role="alert">{cardPaymentError}</p>
                ) : null}
              </div>

              {bookMutation.isError ? (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
                  {formatBackendError(bookMutation.error)}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={!quote || bookMutation.isPending || cardPaymentInProgress}
                className="w-full px-6 py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="pb-submit"
              >
                {bookMutation.isPending || cardPaymentInProgress ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Package className="w-5 h-5" />
                )}
                {quote
                  ? paymentMethod === 'CARD'
                    ? cardPaymentReference
                      ? `Book parcel (${naira(quote.amountNgn)} paid)`
                      : `Pay ${naira(quote.amountNgn)} with card`
                    : `Pay ${naira(quote.amountNgn)} &amp; Book`
                  : 'Submit booking'}
              </button>
            </form>
          ) : null}

          <div className="flex items-center justify-between mt-8 pt-5 border-t border-border">
            {step > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 border border-border rounded-lg hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <div />
            )}
            {step < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed}
                className={`flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-lg ${
                  canProceed ? 'bg-nipost-green text-white hover:bg-nipost-green-dark' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
